// src/pages/Auth/ResetPassword.js
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/axiosConfig";
import "./Auth.css";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    } else {
      setError("Invalid reset link");
    }
  }, [searchParams]);

  const verifyToken = async (token) => {
    try {
      const response = await api.post("/change-password/verify", {
        token: token
      });

      if (response.status === 200) {
        setTokenValid(true);
        setEmail(response.data.data?.email || "");
      } else {
        setError("Invalid or expired reset token");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        "Invalid or expired reset token"
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/change-password/reset", {
        token: token,
        newpassword: password
      });

      if (response.status === 200) {
        setSuccess("Password updated successfully! Redirecting to login...");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(response.data?.message || "Failed to reset password");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      setError(
        err.response?.data?.message || 
        err.response?.data || 
        "Unable to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  if (!tokenValid && !error) {
    return (
      <div className="login-container">
        <div className="login-right-panel">
          <div className="loading-message">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <div className="animated-background">
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
          <div className="brand-section">
            <h1 className="brand-title">Set New Password</h1>
            <p className="brand-subtitle">Create a strong new password for your account</p>
          </div>
        </div>
      </div>

      <div className="login-right-panel">
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-header">
            <h2>Reset Password</h2>
            <p>Enter your new password below</p>
            {email && (
              <p className="email-display">
                <i className="fas fa-user"></i>
                {email}
              </p>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="input-with-icon password-input-wrapper">
              <i className="fas fa-lock"></i>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength="6"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={toggleShowPassword}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="input-with-icon">
              <i className="fas fa-lock"></i>
              <input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength="6"
              />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading || !tokenValid} 
            className="login-btn"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Resetting Password...
              </>
            ) : (
              "Reset Password"
            )}
          </button>

          <div className="form-footer">
            <button 
              type="button" 
              onClick={() => navigate('/login')} 
              className="back-to-login"
            >
              <i className="fas fa-arrow-left"></i>
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}